import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { GetOrganizationInfo, GetStates } from "./queries";
import { UpdateAccountInfo, CreateOriginAddress } from "./mutations";
import UploadHelper from "../../../helpers/upload";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useInputState from "../../../hooks/useInputState";
import useMaskedInputState from "../../../hooks/useMaskedInputState";
import { useLocation } from "react-router";
import useTagAccount from "../../../hooks/useTagAccount";

const useOrganizationInfo = () => {
  const history = useHistory();
  const location = useLocation();
  const numberOnly = /^[0-9\b]+$/;
  const [name, setName] = useMaskedInputState(undefined, {
    regex: /[^ ]/,
    maxLength: 255
  });
  const [checkbox, setCheckbox] = useState(false);
  const [typePrefilled, setTypePrefilled] = useState(false);
  const [image, setImage] = useState();
  const [imageFile, setImageFile] = useInputState();
  const [bio, setBio] = useMaskedInputState(undefined, {
    regex: /[^ ]/,
    maxLength: 4000
  });
  const [website, setWebsite] = useMaskedInputState(undefined, {
    regex: /[^ ]/,
    maxLength: 255
  });
  const [contactNumber, setContactNumber] = useMaskedInputState("", {
    regex: numberOnly,
    maxLength: 10
  });
  const [taxId, setTaxId] = useInputState();
  const [city, setCity] = useInputState();
  const [zip, setZip] = useMaskedInputState("", {
    regex: numberOnly,
    maxLength: 5
  });
  const [state, setState] = useInputState("");
  const [street, setStreet] = useInputState();
  const [type, setType] = useInputState();
  const [error, setError] = useState();
  const [isFormValid, setIsFormValid] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { data: statesData } = useQuery(GetStates);
  const query = new URLSearchParams(location.search);
  const industryType = query.get("industry");
  const statesList = [
    { label: "State", value: "", disabled: true },
    ...((statesData &&
      statesData.states.map(({ id, name }) => ({ value: id, label: name }))) ||
      [])
  ];

  const typeList = [
    { label: "Type", value: "", disabled: true },
    ...["Individual", "Corporation", "LLC", "Political", "NonProfit"]
  ];

  const prefillOrganization =
    getHasCompletedTutorial("Brand Dashboard Organization Info") ||
    getHasCompletedTutorial("Brand Dashboard Profile");

  const { data, loading } = useQuery(GetOrganizationInfo, {
    onError: (err) => {
      setError(getError());
    }
  });

  const savedOrganizationInfo = data && data.accounts && data.accounts[0];
  const savedOriginAddress =
    savedOrganizationInfo && savedOrganizationInfo.originAddress;
  const isTaxIdProvided =
    savedOrganizationInfo && savedOrganizationInfo.isTaxIdProvided;

  const isNmiAccount =
    savedOrganizationInfo && savedOrganizationInfo.isNmiAccount;

  useEffect(() => {
    if (savedOrganizationInfo && prefillOrganization) {
      setBio(savedOrganizationInfo.bio);
      setImage(savedOrganizationInfo.picture);
      setName(savedOrganizationInfo.name);
      setWebsite(savedOrganizationInfo.website);
      setContactNumber(savedOrganizationInfo.contactNumber);
      setTaxId(isTaxIdProvided ? "*********" : "");
      setType(savedOrganizationInfo.type);

      if (savedOriginAddress) {
        setCity(savedOriginAddress.city);
        setZip(savedOriginAddress.postalCode);
        setStreet(savedOriginAddress.street1);
        setState(savedOriginAddress.state.id);
      }
    }
  }, [savedOrganizationInfo, savedOriginAddress]);

  useEffect(() => {
    const validType = typeList.includes(industryType);
    if (validType && !prefillOrganization) {
      setType(industryType);
      setTypePrefilled(true);
    }
  }, [industryType]);

  const formHasValidFields =
    !!image &&
    !!name &&
    !!type &&
    !!website &&
    !!contactNumber &&
    !!street &&
    !!city &&
    !!state &&
    !!zip &&
    !!bio &&
    !!checkbox;

  const [createAddress] = useMutation(CreateOriginAddress);
  const { tagAccount } = useTagAccount();
  const [updateAccount] = useMutation(UpdateAccountInfo, {
    refetchQueries: [{ query: GetOrganizationInfo }]
  });

  const onImageChange = (file) => {
    const jsFile = new File(file, "profile_image");
    setImageFile(jsFile);
    const dataReader = new FileReader();
    dataReader.onload = (event) => {
      setImage(event.target.result);
    };
    dataReader.readAsDataURL(jsFile);
  };

  async function checkUserTutorial() {
    const hasCompletedOnboardingStep = getHasCompletedTutorial(
      "Brand Dashboard Personal Info"
    );
    if (!hasCompletedOnboardingStep) {
      await completeTutorial({
        variables: { name: "Brand Dashboard Personal Info", version: 1 }
      });
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsFormValid(formHasValidFields);
    debugger;

    if (formHasValidFields) {
      try {
        setUpdateLoading(true);
        let picture = undefined;
        const hasCompletedOnboardingStep = getHasCompletedTutorial(
          "Brand Dashboard Organization Info"
        );

        if (
          image &&
          (savedOrganizationInfo && savedOrganizationInfo.picture !== image)
        ) {
          const { signedUrl, url } = await UploadHelper.getSignedUrl(
            UploadHelper.AVATAR
          );
          await UploadHelper.uploadToS3(signedUrl, imageFile);
          picture = url;
        }

        const {
          data: { updateAccountInfo: accountStatus }
        } = await updateAccount({
          variables: {
            account: {
              bio,
              picture,
              name,
              ...(!isNmiAccount && {
                statementDescriptor: createStatementDescriptor(name)
              }),
              website,
              contactNumber,
              ...(isTaxIdProvided || isNmiAccount ? {} : { taxId }),
              ...(isTaxIdProvided ? {} : { type })
            }
          }
        });

        if (accountStatus === "NotUpdated") {
          setUpdateLoading(false);
          setError(getError());
          return;
        }

        if (!isTaxIdProvided) {
          await tagAccount(type);
        }

        const hasAddedStatementDescriptor = getHasCompletedTutorial(
          "Add Statement Descriptor"
        );

        if (!hasAddedStatementDescriptor) {
          await completeTutorial({
            variables: {
              name: "Add Statement Descriptor",
              version: 1
            }
          });
        }

        const isOriginAddressValid =
          (!!city && savedOriginAddress && savedOriginAddress.city !== city) ||
          (!!state &&
            savedOriginAddress &&
            savedOriginAddress.state.id !== state) ||
          (!!zip &&
            savedOriginAddress &&
            savedOriginAddress.postalCode !== zip) ||
          (!!street &&
            savedOriginAddress &&
            savedOriginAddress.street1 !== street);

        if (!savedOriginAddress || isOriginAddressValid) {
          await createAddress({
            variables: {
              address: {
                city,
                state,
                postalCode: zip,
                street1: street,
                type: "Origin"
              }
            }
          });
        }

        await checkUserTutorial();
        if (!hasCompletedOnboardingStep) {
          await completeTutorial({
            variables: { name: "Brand Dashboard Organization Info", version: 1 }
          });
        }
        setUpdateLoading(false);
        history.push("/register/payment");
      } catch (err) {
        setError(getError());
        setUpdateLoading(false);
      }
    }
  };

  const onBackPress = () => history.push("/register");
  return {
    image,
    bio,
    name,
    website,
    state,
    contactNumber,
    taxId,
    type,
    city,
    zip,
    street,
    setStreet,
    setZip,
    setCity,
    setState,
    setContactNumber,
    setTaxId,
    error,
    setType,
    isFormValid,
    disabled: (!formHasValidFields && !isFormValid) || !checkbox,
    loading,
    updateLoading,
    statesList,
    setCheckbox,
    checkbox,
    onImageChange,
    typeList,
    setName,
    setBio,
    setWebsite,
    buttonText: prefillOrganization ? "UPDATE INFO" : "SIGNUP",
    onSubmit,
    isTaxIdProvided,
    isNmiAccount: savedOrganizationInfo && savedOrganizationInfo.isNmiAccount,
    typeDisabled: isTaxIdProvided || typePrefilled,
    onBackPress
  };
};

const getError = () => {
  return {
    message: (
      <span>
        Something went wrong! Try again or contact{" "}
        {
          <a
            href="https://help.videosquirrel.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            support
          </a>
        }{" "}
        if the problem persists.
      </span>
    )
  };
};

const createStatementDescriptor = (name) => {
  const regex = /[\\'"*<>]/g;
  // length depends on the statement descriptor prefix set at stripe
  const length = 14;
  return name.replace(regex, "").substring(0, length);
};

export default useOrganizationInfo;
